import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Activity, ChevronRight } from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  calculateMHR,
  calculateZoneDurations,
  calculateCalories,
  intensityPercent,
} from "../features/trainingSummary/zoneUtils";
import { extractHrStats } from "../utils/hrSeries";
import { HrMetricIcon } from "./HrMetricIcon";
import { DurationMetricValue } from "./DurationMetricValue";
import "./SessionTrainingSummary.css";

function MetricTile({ label, value, unit, subtitle, dimmed = false, variant = "max" }) {
  return (
    <div
      className={`session-training-summary__metric session-training-summary__metric--${variant}${
        dimmed ? " session-training-summary__metric--dimmed" : ""
      }`}
    >
      <div className="session-training-summary__metric-label-row">
        <span className="session-training-summary__metric-icon" aria-hidden="true">
          <HrMetricIcon variant={variant} size={15} />
        </span>
        <span className="session-training-summary__metric-label">{label}</span>
      </div>
      <div className="session-training-summary__metric-value-row">
        <span className="session-training-summary__metric-value">{value}</span>
        {unit ? <span className="session-training-summary__metric-unit">{unit}</span> : null}
      </div>
      {subtitle ? (
        <span className="session-training-summary__metric-subtitle">{subtitle}</span>
      ) : null}
    </div>
  );
}

function sortRankingRows(rows, metricKey) {
  return [...rows]
    .filter((row) => row.hasHr && Number(row[metricKey]) > 0)
    .sort((a, b) => Number(b[metricKey]) - Number(a[metricKey]));
}

function RankingBlock({ title, rows, metricKey, unit }) {
  if (!rows.length) return null;
  return (
    <div className="session-training-summary__ranking-block">
      <h4 className="session-training-summary__ranking-title">{title}</h4>
      <ol className="session-training-summary__ranking-list">
        {rows.map((row, index) => (
          <li key={`${row.label}-${index}`} className="session-training-summary__ranking-row">
            <span className="session-training-summary__ranking-pos">{index + 1}</span>
            <span className="session-training-summary__ranking-name">{row.label}</span>
            <span className="session-training-summary__ranking-value">
              {row[metricKey]}
              {unit ? <span className="session-training-summary__ranking-unit">{unit}</span> : null}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function SessionTrainingSummary({
  loading = false,
  coachAllPlayers = false,
  sessionWideStats = null,
  hrInfo = null,
  physiology = { age: 30, gender: "male", weightKg: 70 },
  playerName = null,
  onTap = null,
  tapDisabled = false,
  allPlayersSessionRows = [],
  allPlayersSessionLoading = false,
}) {
  const { t } = useTranslation();

  const stats = useMemo(() => extractHrStats(hrInfo), [hrInfo]);
  const mhr = useMemo(
    () => calculateMHR({ age: physiology.age, gender: physiology.gender }),
    [physiology.age, physiology.gender]
  );

  const maxHr = coachAllPlayers
    ? sessionWideStats?.maxHr ?? null
    : stats.maxHr;
  const avgHr = coachAllPlayers
    ? sessionWideStats?.avgHr ?? null
    : stats.avgHr;

  const totalDurationSec = stats.summaryDurationSec || 0;
  const zoneDurations = useMemo(
    () =>
      calculateZoneDurations({
        mhr,
        hrPoints: stats.hrPoints,
        totalDurationSec: totalDurationSec > 0 ? totalDurationSec : stats.hrPoints.length,
      }),
    [mhr, stats.hrPoints, totalDurationSec]
  );
  const calories = useMemo(
    () =>
      coachAllPlayers
        ? null
        : calculateCalories({
            zoneDurations,
            weightKg: physiology.weightKg ?? 70,
          }),
    [coachAllPlayers, zoneDurations, physiology.weightKg]
  );

  const maxIntensity = intensityPercent(maxHr, mhr);
  const avgIntensity = intensityPercent(avgHr, mhr);
  const hasDuration = totalDurationSec > 0;
  const hasHrData =
    coachAllPlayers ||
    stats.maxHr > 0 ||
    stats.avgHr > 0 ||
    stats.hrPoints.length > 0;
  const isTappable = typeof onTap === "function";
  const showChevron = isTappable && !tapDisabled && !loading && !coachAllPlayers;

  const rankedMaxIntensity = useMemo(
    () => sortRankingRows(allPlayersSessionRows, "maxIntensity"),
    [allPlayersSessionRows]
  );
  const rankedAvgIntensity = useMemo(
    () => sortRankingRows(allPlayersSessionRows, "avgIntensity"),
    [allPlayersSessionRows]
  );
  const rankedCalories = useMemo(
    () => sortRankingRows(allPlayersSessionRows, "calories"),
    [allPlayersSessionRows]
  );

  const hasAllPlayersRankings =
    rankedMaxIntensity.length > 0 ||
    rankedAvgIntensity.length > 0 ||
    rankedCalories.length > 0;

  if (!loading && !coachAllPlayers && !hasHrData) {
    return null;
  }

  if (
    coachAllPlayers &&
    !allPlayersSessionLoading &&
    !hasAllPlayersRankings &&
    !(maxHr > 0 || avgHr > 0)
  ) {
    return null;
  }

  const title = coachAllPlayers
    ? t("session_training_summary_all_players_title", "Session Summary — All Players")
    : playerName
      ? t("session_training_summary_player_title", "{{name}}'s Session Summary", {
          name: playerName,
        })
      : t("session_training_summary_title", "Your Session Summary");

  const content = (
    <>
      <div className="session-training-summary__header">
        <div className="session-training-summary__header-icon" aria-hidden="true">
          <Activity size={18} />
        </div>
        <div className="session-training-summary__header-text">
          <h3 className="session-training-summary__title">{title}</h3>
          <p className="session-training-summary__footnote">
            {coachAllPlayers
              ? t(
                  "session_training_summary_all_players_footnote",
                  "Rankings include players with heart rate data only."
                )
              : t(
                  "session_training_summary_footnote",
                  "Summary computed from session heart rate data."
                )}
          </p>
        </div>
        {showChevron ? (
          <ChevronRight
            className="session-training-summary__chevron"
            size={20}
            aria-hidden="true"
          />
        ) : null}
      </div>

      {loading || allPlayersSessionLoading ? (
        <div className="session-training-summary__loading">
          <CircularProgress size={28} />
        </div>
      ) : coachAllPlayers ? (
        <div className="session-training-summary__rankings">
          <RankingBlock
            title={t("session_results_ranking_max_intensity", "Max intensity")}
            rows={rankedMaxIntensity}
            metricKey="maxIntensity"
            unit="%"
          />
          <RankingBlock
            title={t("session_results_ranking_avg_intensity", "Avg intensity")}
            rows={rankedAvgIntensity}
            metricKey="avgIntensity"
            unit="%"
          />
          <RankingBlock
            title={t("session_results_ranking_calories", "Burned calories")}
            rows={rankedCalories}
            metricKey="calories"
            unit="kcal"
          />
        </div>
      ) : (
        <div className="session-training-summary__metrics-grid">
          <MetricTile
            variant="duration"
            label={t("session_duration", "Duration")}
            value={
              hasDuration ? (
                <DurationMetricValue totalSec={totalDurationSec} />
              ) : (
                "—"
              )
            }
            dimmed={!hasDuration}
          />
          <MetricTile
            variant="calories"
            label={t("calories", "Calories")}
            value={calories > 0 ? calories : "—"}
            unit={calories > 0 ? "kcal" : ""}
            dimmed={!calories}
          />
          <MetricTile
            variant="max"
            label={t("max_hr", "Max HR")}
            value={maxHr > 0 ? maxHr : "—"}
            unit={maxHr > 0 ? "bpm" : ""}
            dimmed={!maxHr}
          />
          <MetricTile
            variant="max_intensity"
            label={t("max_intensity", "Max Intensity")}
            value={maxIntensity > 0 ? maxIntensity : "—"}
            unit={maxIntensity > 0 ? "%" : ""}
            dimmed={!maxHr}
          />
          <MetricTile
            variant="avg"
            label={t("avg_hr", "Avg HR")}
            value={avgHr > 0 ? Math.round(avgHr) : "—"}
            unit={avgHr > 0 ? "bpm" : ""}
            dimmed={!avgHr}
          />
          <MetricTile
            variant="avg_intensity"
            label={t("avg_intensity", "Avg Intensity")}
            value={avgIntensity > 0 ? avgIntensity : "—"}
            unit={avgIntensity > 0 ? "%" : ""}
            dimmed={!avgHr}
          />
        </div>
      )}
    </>
  );

  if (isTappable && !coachAllPlayers) {
    return (
      <button
        type="button"
        className={`session-training-summary session-training-summary--tappable${
          tapDisabled ? " session-training-summary--disabled" : ""
        }`}
        aria-label={title}
        onClick={tapDisabled ? undefined : onTap}
        disabled={tapDisabled || loading}
      >
        {content}
      </button>
    );
  }

  return (
    <section className="session-training-summary" aria-label={title}>
      {content}
    </section>
  );
}

export default SessionTrainingSummary;
